/**
 * Comment Pin Service
 * 
 * Manages comment pins in Firestore for the cross-domain comment system.
 * Works alongside ChatMessageService but uses a separate collection for pin metadata.
 * 
 * Collections:
 * - comment_pins: Stores pin location and metadata
 * - chat_messages: Reuses existing collection for message storage (via ChatMessageService)
 * - chat_channels: Reuses existing collection for channel tracking (via ChatMessageService)
 * 
 * Integration Strategy:
 * 1. Comment pins use the SAME chat_messages and chat_channels collections as billing
 * 2. Differentiated by channelId pattern:
 *    - Billing: spend-chat-{workflowId}-{entryKey}
 *    - Comments: {domain}-page-comment-{urlSlug}-{timestamp}
 * 3. No conflicts - different channelId patterns ensure isolation
 * 
 * Reference: .claude/plans/comment-infra-shared-migration.md
 */

import {
  collection,
  addDoc,
  query,
  where,
  orderBy,
  getDocs,
  deleteDoc,
  updateDoc,
  serverTimestamp,
  type Timestamp,
  onSnapshot,
  type Unsubscribe,
} from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { log } from '@/lib/logger';
import type { CommentPin } from '@/shared/contexts/CommentModeProvider';

// Firestore comment pin document structure
interface CommentPinDocument {
  id?: string;
  pinId: string;              // Unique pin identifier
  x: number;                  // X coordinate
  y: number;                  // Y coordinate
  scrollY: number;            // Scroll position
  pageUrl: string;            // URL path (indexed for queries)
  domain: string;             // Domain context
  createdAt: Timestamp;       // Server timestamp
  createdBy: string;          // Employee ID
  viewportWidth: number;      // For responsive handling
  viewportHeight: number;     // For responsive handling
  resolved: boolean;          // Resolution status
  channelId: string;          // Channel ID for messages
  updatedAt: Timestamp;       // Last update timestamp
}

export class CommentPinService {
  private static readonly PINS_COLLECTION = 'comment_pins';

  /**
   * Generate channel ID for comment pins
   * Pattern: {domain}-page-comment-{pinId}
   * Example: intel-page-comment-pin-1698765432-abc123
   * Uses pin ID for absolute uniqueness and traceability
   */
  static generateChannelId(pinId: string, domain: string, _pageUrl: string): string {
    // Use pin ID directly for maximum uniqueness
    return `${domain}-page-comment-${pinId}`;
  }

  /**
   * Save a comment pin to Firestore
   */
  static async savePin(pin: CommentPin): Promise<void> {
    try {
      log.debug('[COMMENT-PIN-SERVICE] Saving pin to Firestore:', pin.id);

      const pinsRef = collection(db, this.PINS_COLLECTION);
      
      const pinDoc: Omit<CommentPinDocument, 'id'> = {
        pinId: pin.id,
        x: pin.x,
        y: pin.y,
        scrollY: pin.scrollY,
        pageUrl: pin.pageUrl,
        domain: pin.domain,
        createdBy: pin.createdBy,
        viewportWidth: pin.viewportWidth,
        viewportHeight: pin.viewportHeight,
        resolved: pin.resolved || false,
        channelId: this.generateChannelId(
          pin.id,
          pin.domain,
          pin.pageUrl
        ),
        createdAt: serverTimestamp() as Timestamp,
        updatedAt: serverTimestamp() as Timestamp,
      };

      await addDoc(pinsRef, pinDoc);
      log.info('[COMMENT-PIN-SERVICE] Pin saved successfully:', pin.id);
    } catch (error) {
      log.error('[COMMENT-PIN-SERVICE] Error saving pin:', error);
      throw error;
    }
  }

  /**
   * Load comment pins for a specific page URL
   */
  static async loadPinsForPage(pageUrl: string): Promise<CommentPin[]> {
    try {
      log.debug('[COMMENT-PIN-SERVICE] Loading pins for page:', pageUrl);

      const pinsRef = collection(db, this.PINS_COLLECTION);
      const q = query(
        pinsRef,
        where('pageUrl', '==', pageUrl),
        where('resolved', '==', false),
        orderBy('createdAt', 'desc')
      );

      const snapshot = await getDocs(q);
      const pins: CommentPin[] = [];

      snapshot.forEach((doc) => {
        const data = doc.data() as CommentPinDocument;
        const pin: CommentPin = {
          id: data.pinId,
          x: data.x,
          y: data.y,
          scrollY: data.scrollY,
          pageUrl: data.pageUrl,
          domain: data.domain,
          createdAt: data.createdAt ? data.createdAt.toDate().toISOString() : new Date().toISOString(),
          createdBy: data.createdBy,
          viewportWidth: data.viewportWidth,
          viewportHeight: data.viewportHeight,
          resolved: data.resolved || false,
        };
        pins.push(pin);
      });

      log.debug('[COMMENT-PIN-SERVICE] Loaded pins:', pins.length);
      return pins;
    } catch (error) {
      log.error('[COMMENT-PIN-SERVICE] Error loading pins:', error);
      throw error;
    }
  }

  /**
   * Subscribe to real-time pin updates for a page
   */
  static subscribeToPagePins(
    pageUrl: string,
    callback: (pins: CommentPin[]) => void
  ): Unsubscribe {
    const pinsRef = collection(db, this.PINS_COLLECTION);
    const q = query(
      pinsRef,
      where('pageUrl', '==', pageUrl),
      where('resolved', '==', false),
      orderBy('createdAt', 'desc')
    );

    return onSnapshot(q, (snapshot) => {
      const pins: CommentPin[] = [];

      snapshot.docs.forEach((doc) => {
        const data = doc.data() as CommentPinDocument;
        const pin: CommentPin = {
          id: data.pinId,
          x: data.x,
          y: data.y,
          scrollY: data.scrollY,
          pageUrl: data.pageUrl,
          domain: data.domain,
          createdAt: data.createdAt ? data.createdAt.toDate().toISOString() : new Date().toISOString(),
          createdBy: data.createdBy,
          viewportWidth: data.viewportWidth,
          viewportHeight: data.viewportHeight,
          resolved: data.resolved || false,
        };
        pins.push(pin);
      });

      callback(pins);
    }, (error) => {
      log.error('[COMMENT-PIN-SERVICE] Error in pins subscription:', error);
    });
  }

  /**
   * Delete a comment pin
   */
  static async deletePin(pinId: string): Promise<void> {
    try {
      log.info('[COMMENT-PIN-SERVICE] Deleting pin:', pinId);

      const pinsRef = collection(db, this.PINS_COLLECTION);
      const q = query(
        pinsRef,
        where('pinId', '==', pinId)
      );

      const snapshot = await getDocs(q);

      if (!snapshot.empty) {
        const pinDoc = snapshot.docs[0];
        await deleteDoc(pinDoc.ref);
        log.info('[COMMENT-PIN-SERVICE] Pin deleted successfully:', pinId);
      } else {
        log.warn('[COMMENT-PIN-SERVICE] Pin not found for deletion:', pinId);
      }
    } catch (error) {
      log.error('[COMMENT-PIN-SERVICE] Error deleting pin:', error);
      throw error;
    }
  }

  /**
   * Resolve a comment pin
   */
  static async resolvePin(pinId: string): Promise<void> {
    try {
      log.info('[COMMENT-PIN-SERVICE] Resolving pin:', pinId);

      const pinsRef = collection(db, this.PINS_COLLECTION);
      const q = query(
        pinsRef,
        where('pinId', '==', pinId)
      );

      const snapshot = await getDocs(q);

      if (!snapshot.empty) {
        const pinDoc = snapshot.docs[0];
        await updateDoc(pinDoc.ref, {
          resolved: true,
          updatedAt: serverTimestamp(),
        });
        log.info('[COMMENT-PIN-SERVICE] Pin resolved successfully:', pinId);
      } else {
        log.warn('[COMMENT-PIN-SERVICE] Pin not found for resolution:', pinId);
      }
    } catch (error) {
      log.error('[COMMENT-PIN-SERVICE] Error resolving pin:', error);
      throw error;
    }
  }

  /**
   * Get a specific pin by ID
   */
  static async getPin(pinId: string): Promise<CommentPin | null> {
    try {
      const pinsRef = collection(db, this.PINS_COLLECTION);
      const q = query(
        pinsRef,
        where('pinId', '==', pinId)
      );

      const snapshot = await getDocs(q);

      if (snapshot.empty) {
        return null;
      }

      const doc = snapshot.docs[0];
      const data = doc.data() as CommentPinDocument;

      return {
        id: data.pinId,
        x: data.x,
        y: data.y,
        scrollY: data.scrollY,
        pageUrl: data.pageUrl,
        domain: data.domain,
        createdAt: data.createdAt ? data.createdAt.toDate().toISOString() : new Date().toISOString(),
        createdBy: data.createdBy,
        viewportWidth: data.viewportWidth,
        viewportHeight: data.viewportHeight,
        resolved: data.resolved || false,
      };
    } catch (error) {
      log.error('[COMMENT-PIN-SERVICE] Error getting pin:', error);
      return null;
    }
  }

  /**
   * Get all pins for a domain (useful for domain-wide views)
   */
  static async getPinsForDomain(domain: string): Promise<CommentPin[]> {
    try {
      log.debug('[COMMENT-PIN-SERVICE] Loading pins for domain:', domain);

      const pinsRef = collection(db, this.PINS_COLLECTION);
      const q = query(
        pinsRef,
        where('domain', '==', domain),
        orderBy('createdAt', 'desc')
      );

      const snapshot = await getDocs(q);
      const pins: CommentPin[] = [];

      snapshot.forEach((doc) => {
        const data = doc.data() as CommentPinDocument;
        pins.push({
          id: data.pinId,
          x: data.x,
          y: data.y,
          scrollY: data.scrollY,
          pageUrl: data.pageUrl,
          domain: data.domain,
          createdAt: data.createdAt ? data.createdAt.toDate().toISOString() : new Date().toISOString(),
          createdBy: data.createdBy,
          viewportWidth: data.viewportWidth,
          viewportHeight: data.viewportHeight,
          resolved: data.resolved || false,
        });
      });

      log.debug('[COMMENT-PIN-SERVICE] Loaded pins for domain:', pins.length);
      return pins;
    } catch (error) {
      log.error('[COMMENT-PIN-SERVICE] Error loading pins for domain:', error);
      throw error;
    }
  }

  /**
   * Get channel ID for a pin (for chat integration)
   */
  static getChannelIdForPin(pin: CommentPin): string {
    return this.generateChannelId(
      pin.id,
      pin.domain,
      pin.pageUrl
    );
  }
}